import { Injectable, WritableSignal, inject, signal } from '@angular/core';
import { filter, finalize, map, Observable, of, tap } from 'rxjs';
import { SignalsStoreService } from '../shared/signals-store.service';
import { ApiService } from '../shared/api.service';
import { BundleItems, FilterParams, PreOrderData, Product, ProductResume, selectionProducts } from './product.types';
import { RequestHandlerParams } from '../shared/types/api-service.types';
import { ApiResponse } from '../shared/common/types';
import { SidebarService } from '../shared/sidebar/sidebar.service';
import { SidebarCheckList, SidebarFilterTypes, SidebarMenu } from '../shared/sidebar/sidebar.types';
import { NotificationService } from '../shared/notification/notification.service';
import { StockService } from '../stock/stock.service';
import { BundleEditionType } from '../shared/types/order.type';
import { formatDateToReadableString } from '../shared/utils/formatting';
import { DataLayer_CartItem } from '../shared/data-layer/data-layer.types';
import { DataLayerService } from '../shared/data-layer/data-layer.service';

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
  private signalsStoreService = inject(SignalsStoreService)
  private stockService = inject(StockService);
  private apiService = inject(ApiService);
  private sidebarService = inject(SidebarService);
  private notififacionService = inject(NotificationService);
  #dataLayerService = inject(DataLayerService);

  endpoints = {
    products: `/products`,
    productsByCategoryId: `/categories`,
    productsByCategoryIdResume: `/categories-resume`,
    signupProducts: `/products/sign-up`,
    favorites: `/products/favorites`,
    buyAgain: `/products/buy-again`,
    stock: `/stock`,
    bundleSubstitutions: '/bundle-substitutions',
    bundles: '/bundle'
  }

  isLoading: WritableSignal<boolean> = signal(false);
  isLoaded: WritableSignal<boolean> = signal(false);
  productSignal: WritableSignal<any> = signal(null)
  productsSignal: WritableSignal<ProductResume[]> = signal([]);
  private products: ProductResume[] = [];
  productsSelectionListSignal: WritableSignal<selectionProducts> = signal({ onetime: [], subscription: [] });

  #filterFunctions: any = {
    [SidebarFilterTypes.producers]: (ids: number[], product: any) => this.#matchFilter(ids, product.producer ? [product.producer] : []),
    [SidebarFilterTypes.dietRestrictions]: (ids: number[], product: any) => this.#matchFilter(ids, product.dietRestrictions ?? []),
    [SidebarFilterTypes.tags]: (ids: number[], product: any) => this.#matchFilter(ids, product.tags ?? []),
  };

  #mapFilterFunctions: any = {
    [SidebarFilterTypes.producers]: (product: any) => product.producer ? [product.producer.id] : [],
    [SidebarFilterTypes.dietRestrictions]: (product: any) => product.dietRestrictions?.map((x: any) => x.id) ?? [],
    [SidebarFilterTypes.tags]: (product: any) => product.tags?.map((x: any) => x.id) ?? []
  };

  getProduct(productId: number) {
    this.productSignal.set(null);
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: `${this.endpoints.products}/${productId}`,
      apiV3: true
    }

    this.signalsStoreService.isContentLoaded.set(false)

    return this.apiService.handleRequest<ApiResponse<Product>>(params).pipe(
      filter((response: ApiResponse<Product>) => !!response.data),
      tap(({ data: product }: ApiResponse<Product>) => {
        product.price = parseFloat(product.price.toString()).toFixed(2);
        this.#setUpIndividualProductResponse(product);
        this.signalsStoreService.isContentLoaded.set(true);
      }),
    )
  };

  getProductsDetailsByIds(productsIds: number[]) {
    const params: RequestHandlerParams = {
      method: 'POST',
      endpoint: this.endpoints.products,
      apiV3: true,
      body: { productsIds }
    };
    return this.apiService.handleRequest<ApiResponse<Product[]>>(params).pipe(
      filter((response: ApiResponse<Product[]>) => !!response.data?.length),
      map((response: ApiResponse<Product[]>) => response.data),
      finalize(() => {
        this.isLoading.set(false);
        this.isLoaded.set(true);
      })
    )
  }


  getProductById(productId: number, type?: BundleEditionType) {
    this.productSignal.set(null);
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: `${this.endpoints.products}/${productId}${type ? `?type=${type}` : ''}`,
      apiV3: true
    }

    this.signalsStoreService.isContentLoaded.set(false)

    return this.apiService.handleRequest<ApiResponse<Product>>(params).pipe(
      filter((response: ApiResponse<Product>) => !!response.data),
      tap(({ data: product }: ApiResponse<Product>) => {
        product.price = parseFloat(product.price.toString()).toFixed(2);
        this.#setUpIndividualProductResponse(product);
        this.signalsStoreService.isContentLoaded.set(true);
      }),
    )
  };

  getProductByUrl(seoUrl: string, returnError: boolean = false, showErrorMessage: boolean = true): Observable<Product | null> {
    this.productSignal.set(null);
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: `/products/${seoUrl}`,
      apiV3: true,
      returnError,
      showErrorMessage
    }

    this.signalsStoreService.isContentLoaded.set(false)

    return this.apiService.handleRequest<ApiResponse<Product>>(params).pipe(
      map((res: ApiResponse<Product>) => {
        const product = res.data;
        if (!product) {
          this.signalsStoreService.isContentLoaded.set(true);
          return null;
        }
        product.price = parseFloat(product.price.toString()).toFixed(2);
        this.#setUpIndividualProductResponse(product);
        this.signalsStoreService.isContentLoaded.set(true);
        return product;
      })
    );
  };

  getProductsResume(categoryId: number, trackDataLayerEvent: boolean = false) {
    // Vaciamos stock:
    this.clearProductsAndStock();
    this.isLoading.set(true);
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: `${this.endpoints.productsByCategoryIdResume}/${categoryId}`,
      apiV3: true
    };

    return this.apiService.handleRequest<ApiResponse<{ sidebar: any, products: ProductResume[] }>>(params).pipe(
      filter((response: ApiResponse<{ sidebar: any, products: ProductResume[] }>) => !!response.data && !!response.data.products?.length),
      tap((response: ApiResponse<{ sidebar: any, products: ProductResume[] }>) => this.setUpProductsResponse(false, response.data, trackDataLayerEvent)),
      finalize(() => {
        this.isLoading.set(false);
        this.isLoaded.set(true);
      })
    ).subscribe();
  }

  getFavorites() {
    // Vaciamos stock:
    this.clearProductsAndStock();
    this.isLoading.set(true);
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: this.endpoints.favorites,
      apiV3: true
    };
    return this.apiService.handleRequest<ApiResponse<Product[]>>(params).pipe(
      filter((response: ApiResponse<Product[]>) => !!response.data?.length),
      tap((response: ApiResponse<Product[]>) => this.setUpSelectionProducts(response.data)),
      finalize(() => {
        this.isLoading.set(false);
        this.isLoaded.set(true);
      })
    );
  }

  getBuyAgain() {
    // Vaciamos stock:
    this.clearProductsAndStock();
    this.isLoading.set(true)
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: this.endpoints.buyAgain,
      apiV3: true
    };
    return this.apiService.handleRequest<ApiResponse<Product[]>>(params).pipe(
      filter((response: ApiResponse<Product[]>) => !!response.data?.length),
      tap((response: ApiResponse<Product[]>) => this.setUpSelectionProducts(response.data)),
      finalize(() => {
        this.isLoading.set(false);
        this.isLoaded.set(true);
      })
    );
  }

  private setUpSelectionProducts(data: any) {
    let products: selectionProducts = {
      onetime: [],
      subscription: []
    };
    if (data.length) {
      for (const product of data) {
        if (product.isSubscription)
          products.subscription.push(product);
        else
          products.onetime.push(product);
      }
    }
    this.productsSelectionListSignal.set(products);
  }

  getProductsByFilter(filterParams: FilterParams[]) {
    // Vaciamos stock:
    this.clearProductsAndStock();
    this.isLoading.set(true);
    const queryParams = this.setUpFilterQueryParams(filterParams);
    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: `/categories-resume${queryParams}`,
      apiV3: true
    };
    return this.apiService.handleRequest<ApiResponse<{ sidebar: any, products: ProductResume[] }>>(params).pipe(
      filter((response: ApiResponse<{ sidebar: any, products: ProductResume[] }>) => !!response.data && !!response.data.products?.length),
      tap((response: ApiResponse<{ sidebar: any, products: ProductResume[] }>) => this.setUpProductsResponse(true, response.data)),
      finalize(() => {
        this.isLoading.set(false);
        this.isLoaded.set(true);
      })
    ).subscribe();
  }

  private setUpFilterQueryParams(filterParams: FilterParams[]): string {
    let query = '';
    let i = 0;
    for (const filter of filterParams) {
      query += i === 0 ? '?' : '&';
      query += `${filter.type}=${filter.values.join(';')}`;
      i++;
    }
    return query;
  }

  setUpProductsResponse(isFiltering: boolean, data: { sidebar: any, products: ProductResume[] }, trackDataLayerEvent: boolean = false) {

    // const formattedData = data.products.map(p => {
    //   p.preOrder = this.#setUpPreorderProductData(p.preOrder);
    //   return p
    // });

    // const formattedData = data.products.map(p => {
    //   const __detailsMapId = `${p.id}_${p.category?.id || ''}_${p.subcategory?.id || ''}`;
    //   p.__detailsMapId = __detailsMapId;
    //   return p;
    // });

    this.productsSignal.set(data.products);
    this.products = data.products;

    if (data.products && data.products.length) {
      this.signalsStoreService
        .categoryInViewPort
        .set({
          categoryId: +data.products[0].category.id,
          subCategoryId: +data.products[0].subcategory.id,
        });
    }

    let { categories, subcategories, subcategoriesSons, ...res } = data.sidebar;
    this.sidebarService.setUpSidebarData(res);

    if (isFiltering) {
      this.sidebarService.clearFilter();
      this.sidebarService.setUpMenuFilter(categories ?? [], subcategories ?? [], subcategoriesSons ?? []);
    }

    if (trackDataLayerEvent)
      this.#trackItemListViewDataLayerEvent(data);
  }

  #trackItemListViewDataLayerEvent(data: { sidebar: any, products: ProductResume[] }) {
    const dataLayerProducts: DataLayer_CartItem[] = data.products.map(p => {
      let price = +(p.price || 0);
      let originalPrice = +(p.originalPrice || 0) || +(p.price || 0);
      // If the product has attributes:
      if (p.attributes?.length) {
        const attr = p.attributes[0];
        if (attr) {
          const value = attr.values?.[0] || null;
          if (value) {
            price = value.price;
            originalPrice = value.originalPrice || value.price;
          }
        }

      } else if (p.packages?.length) {
        // If the product has packages
        const pack = p.packages[0];
        if (pack) {
          price = pack.price * pack.quantity;
          originalPrice = (pack.originalPrice || pack.price) * pack.quantity;
        }
      }

      price = +(price.toFixed(2))
      originalPrice = +(originalPrice.toFixed(2))

      const discount = originalPrice ? +(originalPrice - price).toFixed(2) : 0;

      return {
        item_id: p.id.toString(),
        item_name: p.name,
        price,
        discount,
        item_category: (p.category?.name || '').replace(/&/g, 'and'),
        item_category2: (p.subcategory?.name || '').replace(/&/g, 'and'),
        item_brand: p.producer?.name || '',
        quantity: 1
      }
    });

    this.#dataLayerService.trackViewItemListEvent({
      ecommerce: {
        item_list_name: data.products[0].category.name,
        items: dataLayerProducts
      }
    })
  }

  #setUpIndividualProductResponse(product: Product) {
    product.preOrder = this.setUpPreorderProductData(product.preOrder);
    this.productSignal.set(product);
  }

  setUpPreorderProductData(preOrder: PreOrderData | null): PreOrderData | null {
    if (!preOrder) return null;
    return {
      ...preOrder,
      _readableEndDate: formatDateToReadableString(preOrder.endDate),
      _readableStartDeliveryDate: formatDateToReadableString(preOrder.startDeliveryDate),
      _readableEndDeliveryDate: formatDateToReadableString(preOrder.endDeliveryDate)
    } as PreOrderData
  }

  filterBySidebarChange() {

    // 1. Validate if there are any filters applied

    const { map, type } = this.signalsStoreService.globalFilter();

    if (!map.size) {

      if (this.productsSignal().length !== this.products.length)
        this.restoreProductsAndSidebar();

      return;
    }

    if (!this.sidebarService.sidebar()?.length)
      return;

    // 2. Filtering of products

    const filteredProducts = this.#filterProductsByMap(map);

    // 3. Update the filter groups

    this.sidebarService.sidebar()
      .forEach(x => {

        if (x.key === type)
          return;

        const products = this.#filterProductsByMap(x.filters);
        const key = x.key.split('-')[0];

        x.sublist
          ?.forEach(y => y.disabled = !products.some(product => this.#mapFilterFunctions[key](product).includes(y.id)));
      });

    // 4. Update the sidebar menu

    this.sidebarService.getSubcategoryFiltered(filteredProducts);

    // 5. Update the products on shop

    this.productsSignal.set(filteredProducts);
  }

  #filterProductsByMap(map: Map<string, number[]>): ProductResume[] {

    const filter = [...map.entries()].map(([name, value]) => ({ name, value }));

    return this.products
      .filter(product => filter.every(x => this.#filterFunctions[x.name](x.value, product)));
  }

  #matchFilter(ids: number[], item: any[]): boolean {

    if (!item.length)
      return false;

    const itemIds = item.map(x => x.id);
    return ids.some(x => itemIds.includes(x));
  }

  private restoreProductsAndSidebar() {
    this.productsSignal.set(this.products);
    this.sidebarService.restoreFilters();
  }

  setFavorite(variantId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.favorites}/${variantId}`,
      method: 'POST',
      apiV3: true,
      showErrorMessage: true
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params).pipe(
      tap((response: ApiResponse<any>) => this.notififacionService.show({ text: response.message, type: 'success' }))
    )
  }

  removeFavorite(variantId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.favorites}/${variantId}`,
      method: 'DELETE',
      apiV3: true,
      showErrorMessage: true
    };

    return this.apiService.handleRequest<ApiResponse<any>>(params);
  }

  private clearProductsAndStock() {
    this.sidebarService.sidebar.set([]);
    this.productsSignal.set([]);
    this.products = [];
    this.productsSelectionListSignal.set({ onetime: [], subscription: [] });
  }

  goToSection(item: SidebarMenu, event?: Event) {

    if (event && item.checked && item.sublist?.length)
      event.preventDefault();

    const parent = document.getElementById('layoutShopMain');
    const element = document.getElementById(item.key as string);

    if (element)
      parent?.scrollTo({
        top: element.offsetTop,
        left: 0,
        behavior: 'instant'
      });
  }

  getBundleSubstitutions(itemId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.products}${this.endpoints.bundleSubstitutions}/${itemId}`,
      method: 'GET',
      apiV3: true,
      showErrorMessage: true
    };

    return this.apiService.handleRequest<ApiResponse<{ suggestedProducts: BundleItems[] }>>(params).pipe(
      filter(res => !!(res.data?.suggestedProducts?.length)),
      map(res => res.data.suggestedProducts.map(p => { p.isSubstitutionProduct = true; return p }))
    );
  }

  getBundelItemsById(bundleId: number) {
    const params: RequestHandlerParams = {
      endpoint: `${this.endpoints.products}${this.endpoints.bundles}/${bundleId}`,
      method: 'GET',
      apiV3: true,
      returnError: true
    };

    return this.apiService.handleRequest<ApiResponse<BundleItems[]>>(params).pipe(map(res => res.data));
  }

  getSignupProducts() {

    const params: RequestHandlerParams = {
      method: 'GET',
      endpoint: this.endpoints.signupProducts,
      apiV3: true
    };

    return this.apiService.handleRequest<ApiResponse<Product[]>>(params).pipe(
      filter((response: ApiResponse<Product[]>) => !!response.data)
    );
  }
}
